import { Affix, Badge, LoadingOverlay } from "@mantine/core";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useStopwatch } from "react-timer-hook";

import { CheckInActiveApi } from "@/domain";
import { useTv } from "@/features/tv/hooks";
import { attendancesApi, CheckInCalled, settingsApi } from "@/store/apis/nextcargo";
import { useSyncTvQuery } from "@/store/apis/nextcargo/_custom/sync";
import { EXPEDITION_SYNC_POOLING_INTERVAL } from "@/store/apis/nextcargo/_custom/sync/sync.constants";
import { companyApi } from "@/store/apis/nextcargo/company/company.api";
import { queueApi } from "@/store/apis/nextcargo/queue/queue.api";

export const Route = createFileRoute("/tv")({
  component: DriversTV,
  loader: async ({ context }) => {
    const {
      store,
      state: { windowLocationHost }
    } = context;
    const dispatch = store.dispatch;

    const companyData = await dispatch(companyApi.endpoints.getCompanyByHost.initiate({ host: windowLocationHost! })).unwrap();
    const companyIdNumber = companyData?.id;

    const fetchServicesStatus = dispatch(attendancesApi.endpoints.getAttendancesTodayByCompany.initiate({ id: companyIdNumber })).unwrap();
    const fetchSettings = dispatch(settingsApi.endpoints.getSettingsByCompany.initiate({ id: companyIdNumber })).unwrap();
    const fetchQueueActive = dispatch(queueApi.endpoints.getQueueActiveByCompany.initiate({ id: companyIdNumber })).unwrap();

    const promises = [fetchSettings, fetchServicesStatus, fetchQueueActive] as const;
    const [companySettings, companyAttendanceStatus, attendance] = await Promise.allSettled(promises);

    const companySettingsData = companySettings.status === "fulfilled" ? companySettings.value : null;
    const companyAttendanceStatusData = companyAttendanceStatus.status === "fulfilled" ? companyAttendanceStatus.value : null;
    const attendanceData =
      attendance.status === "fulfilled"
        ? ([
            attendance.value.original
              .filter((item) => item?.scheduling)
              .filter((item) => item?.scheduling?.truck)
              .map((item) => item?.scheduling?.truck?.id),
            attendance.value.original
          ] as const)
        : ([null, []] as const);
    const [inAttendance, activeQueues] = attendanceData;

    return {
      company: {
        data: companyData,
        settings: companySettingsData,
        attendanceStatus: companyAttendanceStatusData
      },
      attendance: {
        inAttendance,
        activeQueues,
        totalAttended: activeQueues.length
      }
    };
  },
  head: () => ({
    meta: [
      {
        title: "TV"
      }
    ]
  }),
  pendingComponent: () => <LoadingOverlay loaderProps={{ type: "nextCargo" }} overlayProps={{ backgroundOpacity: 0, bg: "white" }} visible />
});

export type DriverTV = CheckInActiveApi;
export type DriverCalled = CheckInActiveApi;
export type DriverCalledQueue = {
  driverCalled: CheckInCalled | null;
  queue: CheckInCalled[];
};
export type TV = {
  driverTv: DriverTV;
  inAttendance: boolean;
};

function DriversTV() {
  const { isLoading } = useSyncTvQuery(undefined, { pollingInterval: EXPEDITION_SYNC_POOLING_INTERVAL, skipPollingIfUnfocused: true });

  const {
    consts: { showTV, driversEmpty }
  } = useTv();

  if (driversEmpty || !showTV || isLoading) return <LoadingOverlay loaderProps={{ type: "nextCargo" }} overlayProps={{ backgroundOpacity: 0, bg: "white" }} visible />;

  return (
    <>
      <Outlet />

      <TimeElapsed />
    </>
  );
}

function TimeElapsed() {
  const { isFetching } = useSyncTvQuery();
  const { seconds, reset } = useStopwatch({ autoStart: true });
  const [, setLastUpdate] = useState<Date>(new Date());

  useEffect(() => {
    if (!isFetching) {
      setLastUpdate(new Date());
      reset();
    }

    return reset;
  }, [isFetching]);

  const elapsedTime = dayjs.duration(seconds, "seconds");
  const formattedElapsed = dayjs.utc(elapsedTime.asMilliseconds()).format("mm:ss");

  return (
    <Affix right={0} bottom={0}>
      <Badge size="lg" variant="white" radius="xs">
        Última atualização - {formattedElapsed}
      </Badge>
    </Affix>
  );
}
